import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {Button, Card, Flex, message, Popconfirm, Space, Table, Tag, Typography} from "antd";
import {DeleteOutlined, EditOutlined, PlusOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import MarketCourseService from "../../../../../services/MarketCourseService";
import {CATEGORY_BY_CODE, FORMAT_BY_CODE, LEVEL_BY_CODE} from "../../../../Courses/courses.enums";
import {previewText} from "../marketCourse.utils";

const {Title, Text} = Typography;

/**
 * Список курсов витрины корпоративного обучения.
 *
 * Данные берём напрямую из MarketCourseService, без слайса: список короткий,
 * грузится целиком одним запросом и никому, кроме этого экрана, не нужен.
 */
const MarketCoursesList = () => {
    const navigate = useNavigate();
    // Одна подписка на переводы на весь компонент: строковые подписи нужны
    // внутри render-колонок, а хук там звать нельзя.
    const {t} = useTranslation();

    const [courses, setCourses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadCourses = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await MarketCourseService.getAllForAdmin();
            setCourses(Array.isArray(data) ? data : []);
        } catch (error) {
            message.error(<IntlMessage id={'marketCoursesPage.loadFailed'}/>, 5);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCourses();
    }, [loadCourses]);

    async function handleDelete(id) {
        try {
            await MarketCourseService.remove(id);
            message.success(<IntlMessage id={'marketCoursesPage.deleted'}/>, 5);
            await loadCourses();
        } catch (error) {
            message.error(<IntlMessage id={'marketCoursesPage.deleteFailed'}/>, 5);
        }
    }

    /** Подпись кода словаря; неизвестный код показываем как есть, чтобы он был заметен. */
    const enumLabel = (dictionary, code) => (dictionary[code] ? t(dictionary[code].labelKey) : (code || '—'));

    const columns = [
        {
            title: <Text strong><IntlMessage id={'marketCoursesPage.column.order'}/></Text>,
            dataIndex: 'sortOrder',
            key: 'sortOrder',
            width: 90,
            align: 'center',
            sorter: (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
            defaultSortOrder: 'ascend',
            render: (value) => <Text>{value ?? 0}</Text>,
        },
        {
            title: <Text strong><IntlMessage id={'marketCoursesPage.column.title'}/></Text>,
            key: 'title',
            width: '32%',
            render: (_, record) => (
                <Flex vertical>
                    <Text>{previewText(record.title)}</Text>
                    <Text type={"secondary"} style={{fontSize: 12}}>{record.slug}</Text>
                </Flex>
            ),
        },
        {
            title: <Text strong><IntlMessage id={'marketCoursesPage.column.category'}/></Text>,
            dataIndex: 'category',
            key: 'category',
            width: '18%',
            render: (code) => <Text>{enumLabel(CATEGORY_BY_CODE, code)}</Text>,
        },
        {
            title: <Text strong><IntlMessage id={'marketCoursesPage.column.format'}/></Text>,
            dataIndex: 'format',
            key: 'format',
            width: '12%',
            render: (code) => <Text>{enumLabel(FORMAT_BY_CODE, code)}</Text>,
        },
        {
            title: <Text strong><IntlMessage id={'marketCoursesPage.column.level'}/></Text>,
            dataIndex: 'level',
            key: 'level',
            width: '12%',
            render: (code) => <Text>{enumLabel(LEVEL_BY_CODE, code)}</Text>,
        },
        {
            title: <Text strong><IntlMessage id={'marketCoursesPage.column.hours'}/></Text>,
            dataIndex: 'durationHours',
            key: 'durationHours',
            width: 90,
            align: 'center',
            render: (value) => <Text>{value ?? 0}</Text>,
        },
        {
            title: <Text strong><IntlMessage id={'marketCoursesPage.column.visible'}/></Text>,
            dataIndex: 'visible',
            key: 'visible',
            width: 110,
            align: 'center',
            render: (value) => (
                <Tag color={value ? 'green' : 'default'}>
                    {value ? t('yes') : t('no')}
                </Tag>
            ),
        },
        {
            title: <Text strong><IntlMessage id={'actions'}/></Text>,
            key: 'actions',
            width: 110,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Space size={"small"}>
                    <Button
                        type={"text"}
                        icon={<EditOutlined/>}
                        title={t('edit')}
                        onClick={() => navigate(`/p/market-courses/${record.id}/edit`)}
                    />
                    <Popconfirm
                        title={<IntlMessage id={'marketCoursesPage.deleteConfirm'}/>}
                        okText={<IntlMessage id={'delete'}/>}
                        cancelText={<IntlMessage id={'cancel'}/>}
                        okButtonProps={{danger: true}}
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button type={"text"} danger icon={<DeleteOutlined/>} title={t('delete')}/>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{width: '100%', height: '100%'}}>
            <Card bordered={false} style={{height: '100%'}}>
                <Flex justify={"space-between"} align={"center"} style={{marginBottom: 16}} gap={20} wrap={"wrap"}>
                    <Title level={3} style={{margin: 0}}>
                        <IntlMessage id={'marketCoursesPage.title'}/>
                    </Title>
                    <Button
                        type={"primary"}
                        icon={<PlusOutlined/>}
                        size={"large"}
                        onClick={() => navigate('/p/market-courses/create')}
                    >
                        <IntlMessage id={'marketCoursesPage.add'}/>
                    </Button>
                </Flex>

                <Table
                    columns={columns}
                    dataSource={courses}
                    loading={isLoading}
                    rowKey={"id"}
                    scroll={{x: 1200}}
                    locale={{emptyText: t('marketCoursesPage.empty')}}
                    pagination={{
                        showSizeChanger: true,
                        defaultPageSize: 20,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total) => <IntlMessage id={'marketCoursesPage.total'} values={{total}}/>,
                    }}
                />
            </Card>
        </div>
    );
};

export default MarketCoursesList;
